<style>
	@import '@/assets/css/custom.css';
</style>

<template>
	<div>
		<Breadcrumbs main="Products" title="Dealer Camera" />

		<div class="container-fluid">
			<div class="row">
				<div class="col-sm-12">

					<div class="card">
						<div class="card-body">
							<div class="d-flex flex-wrap gap-2">
								<button
									type="button"
									class="btn btn-outline-primary"
									:disabled="sampleDownloading"
									@click="downloadSampleExcel"
								>
									Download sample Excel
								</button>
								<button type="button" class="btn btn-primary" @click="openUploadModal">Upload camera data</button>
							</div>
						</div>
					</div>

					<div class="card">
						<div class="card-body">
							<div class="col-md-12 project-list filter-sort">
								<div class="row">
									<div class="col-md-4 d-flex mt-2">
										<label class="d-flex listing-length ms-1"><span>Show</span>
												<select class="form-select" v-model="listing_length" @change="fetchRows()">
												<option value="5">5</option>
												<option value="10">10</option>
												<option value="20">20</option>
											</select>
											<span>entries</span>
										</label>
									</div>
									<div class="col-md-3 mt-2">
										<div class="position-relative dealer-dropdown">
											<input
												class="form-control"
												type="text"
												placeholder="Search and select dealer to show camera data"
												v-model="dealer_search"
												@input="handleDealerSearch"
												@focus="showDealerSuggestions = true"
												@blur="handleDealerInputBlur"
											/>
											<ul
												v-if="showDealerSuggestions && filteredDealerList.length"
												class="list-group position-absolute"
												style="width: 100%; z-index: 9999; max-height: 200px; overflow-y: auto"
											>
												<li
													v-for="dealer in filteredDealerList"
													:key="dealer.id"
													class="list-group-item list-group-item-action"
													@mousedown.prevent="setDealer(dealer)"
												>
													{{ dealerLabel(dealer) }}
												</li>
											</ul>
										</div>
									</div>
									<div class="col-md-2 mt-2">
										<select class="form-select" v-model="status" @change="fetchRows()">
											<option value="">All</option>
											<option value="1">Pending</option>
											<option value="2">Used</option>
										</select>
									</div>
									<div class="col-md-3 mt-2">
										<input
											type="text"
											class="form-control"
											placeholder="Search"
											v-model="keyword"
											@keyup="fetchRows()"
										/>
									</div>
								</div>
							</div>

							<div class="table-responsive product-table mt-3">
								<table class="display" id="dealer-camera-table">
									<thead>
										<tr align="center">
											<th>Sl.No</th>
											<th>Serial number</th>
											<th>Camera</th>
											<th>Camera 2</th>
											<th>Camera 3</th>
											<th>Camera 4</th>
											<th>Camera 5</th>
											<th>Status</th>
											<th>Created at</th>
										</tr>
									</thead>
									<tbody v-if="dealer_id && rows.length">
										<tr align="center" v-for="(row, index) in rows" :key="row.id">
											<td>{{ (from - 1) + index + 1 }}</td>
											<td>{{ row.serial_number }}</td>
											<td>{{ row.camera }}</td>
											<td>{{ row.camera_2 }}</td>
											<td>{{ row.camera_3 }}</td>
											<td>{{ row.camera_4 }}</td>
											<td>{{ row.camera_5 }}</td>
											<td>
												<span class="badge rounded-pill bg-info" v-if="row.status === 1">Pending</span>
												<span class="badge rounded-pill bg-success" v-else>Used</span>
											</td>
											<td>{{ row.converted_created_at }}</td>
										</tr>
									</tbody>
									<tbody v-else>
										<tr>
											<td colspan="9" class="text-center">
												{{ dealer_id ? 'No dealer camera records found' : 'Select a dealer to load data' }}
											</td>
										</tr>
									</tbody>
								</table>
							</div>

							<div class="row mt-2" v-if="dealer_id">
								<div class="col-md-6">
									<p class="text-muted mb-0">
										Showing {{ from || 0 }} to {{ to || 0 }} of {{ total || 0 }} entries
									</p>
								</div>
								<div class="col-md-6">
									<nav aria-label="Page navigation">
										<ul class="pagination justify-content-end">
											<li
												class="page-item"
												v-for="(page, index) in pagination"
												:key="index"
												:class="[{ disabled: !page.url }, { active: page.active }]"
											>
												<a class="page-link" href="#" @click.prevent="fetchRows(page.url)" tabindex="-1">
													<span v-if="index === 0">Prev</span>
													<span v-else-if="index === pagination.length - 1">Next</span>
													<span v-else v-html="page.label"></span>
												</a>
											</li>
										</ul>
									</nav>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<DynamicModal :title="modal_title">
			<template #body>
				<form @submit.prevent="submitImport" enctype="multipart/form-data">
					<div class="row">
						<div class="col-md-12 form-group mt-2">
							<label class="control-label">Dealer<span class="text-danger">*</span></label>
							<div class="position-relative dealer-dropdown">
								<input
									class="form-control"
									type="text"
									placeholder="Search and select dealer"
									v-model="modal_dealer_search"
									:class="{ 'is-invalid': Boolean(importErrors.dealer_id) }"
									@input="handleModalDealerSearch"
									@focus="showModalDealerSuggestions = true"
									@blur="handleModalDealerInputBlur"
								/>
								<ul
									v-if="showModalDealerSuggestions && filteredModalDealerList.length"
									class="list-group position-absolute"
									style="width: 100%; z-index: 9999; max-height: 200px; overflow-y: auto"
								>
									<li
										v-for="dealer in filteredModalDealerList"
										:key="dealer.id"
										class="list-group-item list-group-item-action"
										@mousedown.prevent="setModalDealer(dealer)"
									>
										{{ dealerLabel(dealer) }}
									</li>
								</ul>
							</div>
							<span class="validate-error" v-if="importErrors.dealer_id">
								{{ importErrors.dealer_id[0] }}
							</span>
						</div>
					</div>
					<div class="row">
						<div class="col-md-12 form-group mt-2">
							<label class="control-label">Excel file<span class="text-danger">*</span></label>
							<input
								ref="importFileInput"
								type="file"
								class="form-control"
								accept=".xlsx,.xls"
								:class="{ 'is-invalid': Boolean(importErrors.file) }"
								@change="onImportFileChange"
							/>
							<span class="validate-error" v-if="importErrors.file">
								{{ importErrors.file[0] }}
							</span>
						</div>
					</div>
					<div class="col-md-12 form-group mt-2">
						<button type="submit" class="btn btn-primary me-3" :disabled="importSubmitting">Submit</button>
						<button class="btn btn-light" type="button" @click="hideUploadModal">Close</button>
					</div>
				</form>
			</template>
		</DynamicModal>
	</div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { Modal } from 'bootstrap';
import DynamicModal from '@/components/dynamic_modal.vue';
import { get, getFile, post } from '@/utils/api.js';
import { routes } from '@/constants/routes.js';
import Swal from '@/utils/swal';

const router = useRouter();

let uploadModalInstance = null;

const modal_title = ref('Upload camera data');
const modal_dealer_search = ref('');
const modal_dealer_id = ref('');
const showModalDealerSuggestions = ref(false);
const importFile = ref(null);
const importFileInput = ref(null);
const importErrors = ref({});
const importSubmitting = ref(false);
const sampleDownloading = ref(false);

const dealers = ref([]);
const dealer_search = ref('');
const dealer_id = ref('');
const showDealerSuggestions = ref(false);

const keyword = ref('');
const listing_length = ref(10);
const status = ref('');

const rows = ref([]);
const from = ref(null);
const to = ref(null);
const total = ref(null);
const pagination = ref({});

const dealerLabel = (dealer) => [dealer.first_name, dealer.last_name].filter(Boolean).join(' ');

const filteredDealerList = computed(() => {
	if (!dealer_search.value || dealer_search.value.trim() === '') {
		return dealers.value;
	}
	const q = dealer_search.value.toLowerCase();
	return dealers.value.filter((dealer) => dealerLabel(dealer).toLowerCase().includes(q));
});

const filteredModalDealerList = computed(() => {
	if (!modal_dealer_search.value || modal_dealer_search.value.trim() === '') {
		return dealers.value;
	}
	const q = modal_dealer_search.value.toLowerCase();
	return dealers.value.filter((dealer) => dealerLabel(dealer).toLowerCase().includes(q));
});

const fetchDealers = () => {
	get(routes.getDealers, {}, false)
		.then((response) => {
			if (response.status === 200) {
				dealers.value = response.data.data || [];
			} else {
				Swal.fire('', response.data.message || 'Error fetching dealers', 'error');
			}
		})
		.catch((error) => {
			if (error.response?.status === 401) {
				router.push({ name: 'login' });
			} else {
				Swal.fire('', error.response?.data?.message || 'Error fetching dealers', 'error');
			}
		});
};

const handleDealerSearch = () => {
	if (dealer_search.value && dealer_search.value.trim() !== '') {
		showDealerSuggestions.value = true;
	} else {
		showDealerSuggestions.value = false;
		dealer_id.value = '';
		rows.value = [];
		pagination.value = {};
		from.value = null;
		to.value = null;
		total.value = null;
	}
};

const handleDealerInputBlur = () => {
	setTimeout(() => {
		showDealerSuggestions.value = false;
	}, 200);
};

const setDealer = (dealer) => {
	dealer_search.value = dealerLabel(dealer);
	dealer_id.value = dealer.id;
	showDealerSuggestions.value = false;
	fetchRows();
};

const fetchRows = (url = null) => {
	if (!dealer_id.value) {
		return;
	}

	const params = {
		dealer_id: dealer_id.value,
		limit: listing_length.value,
		keyword: keyword.value,
		status: status.value,
	};

	const requestUrl = url ?? routes.DealerCameraData;

	get(requestUrl, { params }, false)
		.then((response) => {
			if (response.status === 200) {
				const payload = response.data.data?.dealer_camera;
				if (!payload) {
					rows.value = [];
					pagination.value = {};
					from.value = null;
					to.value = null;
					total.value = null;
					return;
				}
				rows.value = payload.data || [];
				pagination.value = payload.links || [];
				from.value = payload.from;
				to.value = payload.to;
				total.value = payload.total;
			} else {
				Swal.fire('', response.data.message || 'Error fetching data', 'error');
			}
		})
		.catch((error) => {
			if (error.response?.status === 422) {
				Swal.fire('', error.response.data.message || 'Validation error', 'error');
			} else if (error.response?.status === 500) {
				Swal.fire('', error.response.statusText || 'Server error', 'error');
			} else if (error.response?.status === 401) {
				Swal.fire('', error.response.statusText || 'Unauthorized', 'error');
				router.push({ name: 'login' });
			} else {
				Swal.fire('', error.response?.data?.message || 'Error occurred', 'error');
			}
		});
};

const showUploadModal = () => {
	if (uploadModalInstance) uploadModalInstance.show();
};

const hideUploadModal = () => {
	if (uploadModalInstance) uploadModalInstance.hide();
};

const resetImportForm = () => {
	modal_dealer_search.value = '';
	modal_dealer_id.value = '';
	importFile.value = null;
	importErrors.value = {};
	showModalDealerSuggestions.value = false;
	if (importFileInput.value) {
		importFileInput.value.value = '';
	}
};

const openUploadModal = () => {
	resetImportForm();
	modal_title.value = 'Upload camera data';
	showUploadModal();
};

const handleModalDealerSearch = () => {
	if (modal_dealer_search.value && modal_dealer_search.value.trim() !== '') {
		showModalDealerSuggestions.value = true;
	} else {
		showModalDealerSuggestions.value = false;
		modal_dealer_id.value = '';
	}
};

const handleModalDealerInputBlur = () => {
	setTimeout(() => {
		showModalDealerSuggestions.value = false;
	}, 200);
};

const setModalDealer = (dealer) => {
	modal_dealer_search.value = dealerLabel(dealer);
	modal_dealer_id.value = dealer.id;
	showModalDealerSuggestions.value = false;
};

const onImportFileChange = (e) => {
	const f = e.target.files?.[0];
	importFile.value = f || null;
};

const downloadSampleExcel = () => {
	sampleDownloading.value = true;
	getFile(routes.DealerCameraSampleExcel, {}, false)
		.then((response) => {
			const blob = new Blob([response.data], {
				type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
			});
			const fileURL = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = fileURL;
			link.setAttribute('download', 'dealer_camera_sample.xlsx');
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(fileURL);
		})
		.catch((error) => {
			if (error.response?.status === 401) {
				router.push({ name: 'login' });
			} else {
				Swal.fire('', error.response?.data?.message || 'Could not download sample file', 'error');
			}
		})
		.finally(() => {
			sampleDownloading.value = false;
		});
};

const submitImport = () => {
	importErrors.value = {};
	if (!modal_dealer_id.value) {
		importErrors.value = { ...importErrors.value, dealer_id: ['Dealer is required'] };
	}
	if (!importFile.value) {
		importErrors.value = { ...importErrors.value, file: ['Excel file is required'] };
	}
	if (importErrors.value.dealer_id || importErrors.value.file) {
		return;
	}

	importSubmitting.value = true;
	const formData = new FormData();
	formData.append('dealer_id', modal_dealer_id.value);
	formData.append('file', importFile.value);

	post(routes.ImportDealerCameraExcel, formData, false)
		.then((response) => {
			if (response.status === 200 && response.data?.success) {
				const importedForDealerId = modal_dealer_id.value;
				const d = response.data.data || {};
				const skipped = Array.isArray(d.skipped) ? d.skipped : [];
				let detail = `Imported: ${d.imported_count ?? 0}. Skipped: ${d.skipped_count ?? 0}.`;
				if (skipped.length) {
					detail += `<br/><small>${skipped.map((s) => (typeof s === 'string' ? s : JSON.stringify(s))).join('<br/>')}</small>`;
				}
				Swal.fire({
					icon: 'success',
					title: response.data.message || 'Import completed',
					html: detail,
				});
				hideUploadModal();
				resetImportForm();
				if (dealer_id.value && String(dealer_id.value) === String(importedForDealerId)) {
					fetchRows();
				}
			} else {
				Swal.fire('', response.data?.message || 'Import failed', 'error');
			}
		})
		.catch((error) => {
			if (error.response?.status === 422) {
				const msg = error.response.data.message;
				importErrors.value =
					msg && typeof msg === 'object' && !Array.isArray(msg) ? msg : {};
				if (typeof msg === 'string') {
					Swal.fire('', msg, 'error');
				}
			} else if (error.response?.status === 401) {
				router.push({ name: 'login' });
			} else {
				Swal.fire('', error.response?.data?.message || 'Import failed', 'error');
			}
		})
		.finally(() => {
			importSubmitting.value = false;
		});
};

onMounted(() => {
	uploadModalInstance = new Modal(document.getElementById('dynamicmodal'));
	fetchDealers();
});
</script>
